import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import Table from "@mui/material/Table";
import { useNavigate } from "react-router-dom";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import ColourConstants from "helpers/colourConstants";
import PopupMenu from "components/Elements/PopupMenu";
import { makeStyles } from "tss-react/mui";
import TableStyle from "styles/application/TableStyle";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { appPath, servicesPath } from "helpers/routePaths";
// Icon imports
import { ReactComponent as MenuIcon } from "assets/icons/3dot-icon.svg";
import AutoFitContentInScreen from "components/Layouts/AutoFitContentInScreen";
import { getServicesList, getServicesSearch } from "services/services/serviceLists/index";
import { statusTypeClassification } from "helpers/constants";
import EMICheckbox from "components/Elements/EMICheckbox";
import { defaultPageSize, createTableHeaderClickHandler } from "helpers/utils";
import { showError } from "../../../redux/common/actions";
import { useDispatch } from "react-redux";
import { useFilters } from "components/Elements/Filters";

// Init styled components
const AT = TableStyle();

// Size constant
const MAX_LOGO_HEIGHT = 47;

const useStyles = makeStyles()((theme) => ({
	tableBody: {
		whiteSpace: "noWrap",
	},

	lastCell: {
		borderBottom: "none",
	},
	table: {
		borderStyle: "solid",
		fontFamily: "Roboto Condensed",
		fontSize: 14,
		overflowX: "auto",
		borderColor: ColourConstants.tableBorder,
		borderWidth: 1,
		borderRadius: 0,
	},
	tableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackground,
		fontWeight: "bold",
		borderRightColor: "#979797",
		borderRightStyle: "solid",
		borderRightWidth: "1px",
	},
	selectedTableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackgroundSelected,
		fontWeight: "bold",
		color: "#FFFFFF",
	},
	nameRow: {
		width: "200px",
		height: "10px",
		lineHeight: "1rem",
	},
	clientsRow: {
		width: "20%",
	},
	dataCell: {
		height: 40,
	},
	nameLink: {
		color: ColourConstants.activeLink,
	},
	clientLogo: {
		maxHeight: MAX_LOGO_HEIGHT,
	},
	noImage: {
		color: ColourConstants.commonText,
		opacity: "50%",
	},
}));

const ServiceListTable = forwardRef(({
	data,
	page,
	setData,
	columns,
	headers,
	searchText,
	handleSort,
	searchQuery,
	searchedData,
	setSearchData,
	handleDeleteDialogOpen,
	handleChnageStatus,
	formattedData,
	countOFService,
	siteAppID,
	status,
	department,
	date,
	setDataForFetchingService,
	currentTableSort,
	selectedServices,
	handleSelectService,
	content,
	isReadOnly,
	...props
}, ref) => {
	// Init hooks
	const { classes, cx } = useStyles();
	const navigate = useNavigate();
	const { filterValues, buildUrlParams } = useFilters();

	// Init State
	const [selectedData, setSelectedData] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [scrollEvent, setScrollEvent] = useState(window);
	const scrollRef = useRef(true);
	const dispatch = useDispatch();

	const { hasMore, loading, gotoTop, handleScroll } = useInfiniteScroll(
		data,
		countOFService,
		async (pageSize, prevData, name) =>
			await onPageChange(pageSize + 1, prevData, name),
		page,
		searchText,
		scrollEvent,
		{
			...buildUrlParams(),
			sortField: currentTableSort[0],
			sort: currentTableSort[1],
			search: searchText,
			page,
			chips: props.chips || [],
		}
	);

	// Expose gotoTop function to parent component
	useImperativeHandle(ref, () => ({
		gotoTop
	}));

	useEffect(() => {
		if (data) {
			if (scrollRef.current === true) {
				document
					.getElementById("table-scroll-wrapper-container")
					.addEventListener("scroll", (e) =>
						handleScroll(e, "table-scroll-wrapper-container")
					);
				setScrollEvent(
					document.getElementById("table-scroll-wrapper-container")
				);
				scrollRef.current = false;
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	//Pagination

	const onPageChange = async (p, prevData, name) => {
		try {
			const currentFilters = buildUrlParams();
			const response = await getServicesSearch({
				pageNumber: p,
				pageSize: defaultPageSize(),
				search: searchText,
				...currentFilters,
				sortField: currentTableSort[0],
				sort: currentTableSort[1],
				chips: props.chips || [],
				siteAppId: siteAppID,
				...name,
			});
			if (response.status) {
				// Ensure response.data is an array
				const responseData = Array.isArray(response.data) ? response.data : [];

				setDataForFetchingService((prev) => ({ ...prev, pageNumber: p }));

				// Ensure prevData is treated as an array
				const newData = Array.isArray(prevData) ? [...prevData, ...responseData] : [...responseData];
				setData(newData);
				response.data = newData;

				return response;
			} else {
				throw new Error(response);
			}
		} catch (err) {
			dispatch(showError(`Failed to load ${content}`));
			return err;
		}
	};
	// Handlers
	const handleSortClick = createTableHeaderClickHandler(currentTableSort, handleSort);
	return (
		<>
			<AutoFitContentInScreen containsTable={true}>
				<Table aria-label="Table" className={classes.table}>
					<AT.TableHead className={classes.taskHeader}>
						<TableRow className={classes.tableHead}>
							{headers.map((header, index) => {
								// Disable sorting for these columns temporarily
								const disableSortingColumns = ["percentageComplete", "percentageOverTime", "minutesOverTime"];
								const isSortingDisabled = disableSortingColumns.includes(columns[index]);
								
								return (
								<TableCell
									key={header.id}
									onClick={() => {
										if (!isSortingDisabled) {
											handleSortClick(columns[index]);
										}
									}}
									style={{ 
										width: header?.width || "auto",
										cursor: isSortingDisabled ? "default" : "pointer"
									}}
									className={cx(classes.nameRow, {
										[classes.selectedTableHeadRow]:
											currentTableSort[0] === columns[index] && !isSortingDisabled,
										[classes.tableHeadRow]:
											currentTableSort[0] !== columns[index] || isSortingDisabled,
									})}
								>
									<AT.CellContainer
										className="flex justify-between"
										style={index === 0 ? { marginLeft: "38px" } : {}}
									>
										{header.name}
										{!isSortingDisabled && (
											<div className="arrow">
												<AT.DescArrow
													fill={
														currentTableSort[0] === columns[index] &&
														currentTableSort[1] === "asc"
															? "#D2D2D9"
															: "#F9F9FC"
													}
													className="arrowUp"
												/>
												<AT.DefaultArrow
													fill={
														currentTableSort[0] === columns[index] &&
														currentTableSort[1] === "desc"
															? "#D2D2D9"
															: "#F9F9FC"
													}
													className="arrowDown"
												/>
											</div>
										)}
									</AT.CellContainer>
								</TableCell>
								);
							})}
						</TableRow>
					</AT.TableHead>
					<TableBody className={classes.tableBody}>
						{formattedData(data, navigate)?.map((row, index) => (
							<TableRow key={index}>
								{columns?.map((col, i, arr) => (
									<AT.DataCell key={col}>
										<AT.CellContainer key={col}>
											<AT.TableBodyText>
												{col === "workOrder" ? (
													<span
														style={{
															display: "flex",
															flexDirection: "space-around",
															alignItems: "center",
															gap: 10,
														}}
													>
														<EMICheckbox
															state={
																Array.isArray(data) && data[index] &&
																selectedServices.findIndex(
																	(d) => d.id === data[index].id
																) !== -1
															}
															changeHandler={({ target: { checked } }) => {
																if (Array.isArray(data) && data[index]) {
																	handleSelectService(data[index], checked);
																}
															}}
														/>

														{col === "modelName"
															? row[col] + " " + row["model"]
															: row[col]}
													</span>
												) : col === "modelName" ? (
													row[col] + " " + (row["model"] ?? "")
												) : (
													row[col]
												)}
											</AT.TableBodyText>

											{arr.length === i + 1 ? (
												<AT.DotMenu
													onClick={(e) => {
														setAnchorEl(
															anchorEl === e.currentTarget
																? null
																: e.currentTarget
														);
														setSelectedData(
															anchorEl === e.currentTarget ? null : index
														);
													}}
												>
													<AT.TableMenuButton>
														<MenuIcon />
													</AT.TableMenuButton>

													<PopupMenu
														index={index}
														selectedData={selectedData}
														anchorEl={anchorEl}
														isLast={
															searchQuery === ""
																? index === data.length - 1
																: index === searchedData.length - 1
														}
														id={row.id}
														clickAwayHandler={() => {
															setAnchorEl(null);
															setSelectedData(null);
														}}
														menuData={[
															{
																name: isReadOnly ? "View" : "Edit",
																handler: () => {
																	navigate(
																		`${appPath}${servicesPath}/${row.id}`
																	);
																},
																isDelete: false,
																showInStatus: "Edit",
															},
															{
																name: "Cancel",
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["S"],
																otherDetail: "X",
															},
															{
																name: "Cancel Checkout",
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["O", "H"],
																otherDetail: "R",
															},
															{
																name: "Reset",
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["I", "H"],
																otherDetail: "R",
															},
															{
																name: "Complete",
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["T"],
																tasksSkipped: true,
																otherDetail: "C",
															},
															{
																name: "Incomplete",
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["T"],
																otherDetail: "N",
															},
															{
																name: "Completed by Paper",
																handler: handleChnageStatus,
																isDelete: false,
																showInStatus: ["S"],
																otherDetail: "P",
															},
															{
																name: "Remove",
																handler: handleDeleteDialogOpen,
																isDelete: true,
																showInStatus: ["S"],
															},
														].filter((x) => {
															if (x.showInStatus === "Edit") return true;
															if (isReadOnly) return false;
															if (
																x.name === "Complete" &&
																x.showInStatus === data[index]["status"] &&
																x?.tasksSkipped === true
															) {
																return true;
															}
															if (
																x.showInStatus?.includes(data[index]["status"])
															)
																return true;
															return false;
														})}
													/>
												</AT.DotMenu>
											) : null}
										</AT.CellContainer>
									</AT.DataCell>
								))}
							</TableRow>
						))}
					</TableBody>

					{/* scroll to load */}
					{loading && (
						<div style={{ padding: "16px 10px" }}>
							<b>Loading...</b>
						</div>
					)}
				</Table>
				{!hasMore && (
					<div
						style={{ textAlign: "center", padding: "16px 10px" }}
						className="flex justify-center"
					>
						<span
							className="link-color ml-md cursor-pointer"
							onClick={() => gotoTop()}
						>
							Go to top
						</span>
					</div>
				)}
			</AutoFitContentInScreen>
		</>
	);
});

export default ServiceListTable;
